import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Avatar, Image } from '@/components/ui/image';
import PostMediaUploader from '@/components/common/PostMediaUploader';
import ShareButton from '@/components/common/ShareButton';
import { formatLocalDateTime } from '@/utils/dateUtils';
import { Plus, User, Heart, Star, UserPlus, UserCheck, MessageCircle, Users, Sparkles, Leaf, ArrowRight, ArrowLeft, BookOpen, Calendar, Utensils, Film, Newspaper, TreePine } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') || null;
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPost, setNewPost] = useState({ content: '', images: [], video_url: '', tags: [], category: '' });
  const [tagInput, setTagInput] = useState('');
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // 社区分类 - 四象限 (使用翻译)
  const communityCategories = [
    {
      id: 'vegan-health',
      titleKey: 'community.categories.veganHealth.title',
      titleEnKey: 'community.categories.veganHealth.titleEn',
      subtitleKey: 'community.categories.veganHealth.subtitle',
      descriptionKey: 'community.categories.veganHealth.description',
      icon: Utensils,
      image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1200&q=80',
      color: 'from-green-900 to-emerald-800',
      bgColor: 'bg-green-600'
    },
    {
      id: 'film-theater',
      titleKey: 'community.categories.filmTheater.title',
      titleEnKey: 'community.categories.filmTheater.titleEn',
      subtitleKey: 'community.categories.filmTheater.subtitle',
      descriptionKey: 'community.categories.filmTheater.description',
      icon: Film,
      image: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1200&q=80',
      color: 'from-rose-900 to-pink-800',
      bgColor: 'bg-rose-600'
    },
    {
      id: 'books-magazines',
      titleKey: 'community.categories.booksMagazines.title',
      titleEnKey: 'community.categories.booksMagazines.titleEn',
      subtitleKey: 'community.categories.booksMagazines.subtitle',
      descriptionKey: 'community.categories.booksMagazines.description',
      icon: Newspaper,
      image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1200&q=80',
      color: 'from-amber-900 to-orange-800',
      bgColor: 'bg-amber-600'
    },
    {
      id: 'ecology-environment',
      titleKey: 'community.categories.ecologyEnvironment.title',
      titleEnKey: 'community.categories.ecologyEnvironment.titleEn',
      subtitleKey: 'community.categories.ecologyEnvironment.subtitle',
      descriptionKey: 'community.categories.ecologyEnvironment.description',
      icon: TreePine,
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&q=80',
      color: 'from-teal-900 to-cyan-800',
      bgColor: 'bg-teal-600'
    }
  ];

  const currentCategory = communityCategories.find(c => c.id === activeCategory);

  useEffect(() => {
    fetchPosts();
  }, [activeCategory, token]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API}/posts`;
      if (activeCategory) {
        url += `?category=${activeCategory}`;
      }
      const res = await axios.get(url, { headers });
      setPosts(res.data);
    } catch (err) {
      console.error('Failed to fetch posts', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUser = async (userId, e) => {
    e?.stopPropagation();
    if (!user) {
      toast.error(t('community.loginRequired'));
      navigate('/login');
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/users/${userId}/follow`, {}, { headers });
      setPosts(posts.map(p => p.user_id === userId ? { ...p, user_is_following: res.data.following } : p));
      toast.success(res.data.following ? t('community.followSuccess') : t('community.unfollowSuccess'));
    } catch (err) {
      toast.error(t('community.operationFailed'));
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.content.trim()) {
      toast.error(t('community.enterContent'));
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const postData = { ...newPost };
      if (activeCategory) {
        postData.category = activeCategory;
      }
      await axios.post(`${API}/posts`, postData, { headers });
      toast.success(t('community.publishSuccess'));
      setShowCreatePost(false);
      setNewPost({ content: '', images: [], video_url: '', tags: [], category: '' });
      fetchPosts();
    } catch (err) {
      toast.error(t('community.publishFailed'));
    }
  };

  const handleLike = async (postId) => {
    if (!user) {
      toast.error(t('community.loginRequired'));
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/posts/${postId}/like`, {}, { headers });
      setPosts(posts.map(p => p.id === postId ? { ...p, likes_count: res.data.likes_count, is_liked: res.data.liked } : p));
    } catch (err) {
      console.error('Like failed', err);
    }
  };

  const handleFavorite = async (postId) => {
    if (!user) {
      toast.error(t('community.loginRequired'));
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/posts/${postId}/favorite`, {}, { headers });
      setPosts(posts.map(p => p.id === postId ? { ...p, favorites_count: res.data.favorites_count, is_favorited: res.data.favorited } : p));
    } catch (err) {
      console.error('Favorite failed', err);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !newPost.tags.includes(tagInput.trim())) {
      setNewPost({ ...newPost, tags: [...newPost.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setNewPost({ ...newPost, tags: newPost.tags.filter(t => t !== tag) });
  };

  const selectCategory = (categoryId) => {
    setSearchParams({ category: categoryId });
  };

  const clearCategory = () => {
    setSearchParams({});
  };

  // 如果选中了某个板块，显示板块详情页
  if (activeCategory && currentCategory) {
    const categoryTitle = t(currentCategory.titleKey);
    const categoryTitleEn = t(currentCategory.titleEnKey);
    const categorySubtitle = t(currentCategory.subtitleKey);
    const categoryDescription = t(currentCategory.descriptionKey);
    
    return (
      <div className="min-h-screen bg-amber-50 pt-16" data-testid="community-category-page">
        {/* 板块Hero */}
        <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img loading="lazy"
              src={currentCategory.image}
              alt={categoryTitle}
              className="w-full h-full object-cover"
            />
            <div className={`absolute inset-0 bg-gradient-to-b ${currentCategory.color} opacity-80`} />
          </div>

          <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
            {/* 返回按钮 */}
            <button 
              onClick={clearCategory}
              className="absolute top-4 left-4 md:top-8 md:left-8 flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>{t('community.backToCommunity')}</span>
            </button>

            <div className="mb-6 flex justify-center">
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <currentCategory.icon className="w-8 h-8 text-white" />
              </div>
            </div>
            <p className="text-sm text-white/60 tracking-widest uppercase mb-4">
              {categoryTitleEn}
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-white mb-4">
              {categoryTitle}
            </h1>
            <p className="text-xl text-white/70 font-light mb-2">
              {categorySubtitle}
            </p>
            <p className="text-white/50">
              {categoryDescription}
            </p>
          </div>
        </section>

        {/* 发帖按钮 */}
        <div className="bg-amber-50/80 py-6 px-6 border-b border-amber-200">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full ${currentCategory.bgColor} flex items-center justify-center`}>
                <currentCategory.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-amber-900">{categoryTitle}</h3>
                <p className="text-sm text-amber-600">{posts.length} {t('community.postsInCategory')}</p>
              </div>
            </div>
            {user ? (
              <Button onClick={() => setShowCreatePost(true)} className="rounded-full" data-testid="new-post-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('community.publishPost')}
              </Button>
            ) : (
              <Button onClick={() => navigate('/login')} variant="outline" className="rounded-full">
                {t('community.loginToPost')}
              </Button>
            )}
          </div>
        </div>

        {/* 帖子列表 */}
        <section className="py-12 px-6 bg-amber-50/50 min-h-[50vh]">
          <div className="max-w-3xl mx-auto">
            <div className="space-y-6">
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-amber-300 border-t-amber-600 rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-amber-600">{t('community.loadingText')}</p>
                </div>
              ) : posts.length === 0 ? (
                <Card className="text-center py-16 bg-white">
                  <currentCategory.icon className="w-16 h-16 mx-auto text-amber-300 mb-4" />
                  <h3 className="text-lg font-medium text-amber-800 mb-2">{t('community.noContentYet', { category: categoryTitle })}</h3>
                  <p className="text-amber-600 mb-6">{t('community.beFirstToPost')}</p>
                  {user && (
                    <Button onClick={() => setShowCreatePost(true)} className="rounded-full bg-amber-600 hover:bg-amber-700">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('community.publishFirstPost')}
                    </Button>
                  )}
                </Card>
              ) : (
                posts.map((post) => (
                  <PostCard 
                    key={post.id} 
                    post={post} 
                    onLike={handleLike}
                    onFavorite={handleFavorite}
                    onFollow={handleFollowUser}
                    user={user}
                    navigate={navigate}
                    t={t}
                  />
                ))
              )}
            </div>
          </div>
        </section>

        {/* 发帖弹窗 */}
        <CreatePostDialog 
          open={showCreatePost}
          onOpenChange={setShowCreatePost}
          newPost={newPost}
          setNewPost={setNewPost}
          tagInput={tagInput}
          setTagInput={setTagInput}
          addTag={addTag}
          removeTag={removeTag}
          onSubmit={handleCreatePost}
          categoryTitle={categoryTitle}
          t={t}
        />
      </div>
    );
  }

  // 主页面 - 直接显示所有板块（简洁版，无大Hero）
  return (
    <div className="min-h-screen bg-amber-50 pt-16" data-testid="community-page">
      {/* 简洁标题区域 */}
      <section className="relative py-16 px-6 bg-gradient-to-br from-amber-800 via-amber-700 to-amber-900">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-6 flex justify-center">
            <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <Users className="w-8 h-8 text-white" />
            </div>
          </div>
          <p className="text-sm text-white/50 tracking-widest uppercase mb-3">
            Art Community
          </p>
          <h1 className="text-4xl md:text-5xl font-light text-white mb-4">
            {t('community.pageTitle')}
          </h1>
          <p className="text-white/60 max-w-xl mx-auto">
            {t('community.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* 社区分类 - 四象限 */}
      <section className="py-16 px-6 bg-amber-50/50">
        <div className="max-w-7xl mx-auto">

          <div className="grid md:grid-cols-2 gap-6">
            {communityCategories.map((category) => (
              <div
                key={category.id}
                className="group relative aspect-[16/10] rounded-3xl overflow-hidden cursor-pointer"
                onClick={() => selectCategory(category.id)}
                data-testid={`community-${category.id}`}
              >
                <img loading="lazy"
                  src={category.image}
                  alt={t(category.titleKey)}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-70 group-hover:opacity-80 transition-opacity`} />
                
                <div className="relative z-10 h-full flex flex-col justify-between p-8 md:p-10">
                  <div>
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4">
                      <category.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-2">{t(category.titleEnKey)}</p>
                    <h3 className="text-4xl md:text-5xl font-light text-white mb-2">
                      {t(category.titleKey)}
                    </h3>
                    <p className="text-white/80 text-lg mb-1">{t(category.subtitleKey)}</p>
                    <p className="text-white/50 text-sm">{t(category.descriptionKey)}</p>
                  </div>
                </div>

                <div className="absolute bottom-8 right-8 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0">
                  <ArrowRight className="w-5 h-5 text-white" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 加入社区CTA */}
      <section className="py-24 px-6 bg-amber-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-light mb-6">{t('community.joinOurCommunity')}</h2>
          <p className="text-white/60 mb-8 max-w-2xl mx-auto">
            {t('community.joinCommunityDesc')}
          </p>
          {user ? (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {communityCategories.map((cat) => (
                <Button
                  key={cat.id}
                  onClick={() => selectCategory(cat.id)}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 rounded-full"
                >
                  <cat.icon className="w-4 h-4 mr-2" />
                  {t(cat.titleKey)}
                </Button>
              ))}
            </div>
          ) : (
            <Button
              onClick={() => navigate('/login')}
              className="bg-white text-amber-900 hover:bg-white/90 rounded-full px-10 py-6 text-lg"
            >
              <User className="w-5 h-5 mr-2" />
              {t('community.joinNow')}
            </Button>
          )}
        </div>
      </section>
    </div>
  );
};

// 帖子卡片组件
const PostCard = ({ post, onLike, onFavorite, onFollow, user, navigate, t }) => {
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start gap-4 mb-4">
          <Avatar
            src={post.user_avatar}
            name={post.user_name}
            className="w-12 h-12 cursor-pointer"
            onClick={() => navigate(`/profile/${post.user_id}`)}
          />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <span 
                  className="font-semibold text-amber-900 cursor-pointer hover:text-amber-700" 
                  onClick={() => navigate(`/profile/${post.user_id}`)}
                >
                  {post.user_name}
                </span>
                {post.user_title && (
                  <Badge variant="secondary" className="ml-2 text-xs bg-amber-100 text-amber-700">
                    {post.user_title}
                  </Badge>
                )}
              </div>
              {user && user.id !== post.user_id && (
                <Button
                  size="sm"
                  variant={post.user_is_following ? "outline" : "default"}
                  onClick={(e) => onFollow(post.user_id, e)}
                  className="h-8 rounded-full"
                >
                  {post.user_is_following ? (
                    <><UserCheck className="w-3 h-3 mr-1" /> {t('community.following')}</>
                  ) : (
                    <><UserPlus className="w-3 h-3 mr-1" /> {t('community.follow')}</>
                  )}
                </Button>
              )}
            </div>
            <p className="text-sm text-amber-600">
              {formatLocalDateTime(post.created_at)}
            </p>
          </div>
        </div>

        <p className="text-amber-800 mb-4 whitespace-pre-wrap">{post.content}</p>

        {post.images && post.images.length > 0 && (
          <div className={`grid gap-2 mb-4 ${
            post.images.length === 1 ? 'grid-cols-1' : 
            post.images.length === 2 ? 'grid-cols-2' : 
            'grid-cols-3'
          }`}>
            {post.images.map((img, idx) => (
              <Image
                key={idx}
                src={img}
                alt=""
                className="w-full aspect-square object-cover rounded-xl"
              />
            ))}
          </div>
        )}

        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="text-amber-600 border-amber-300">
                #{tag}
              </Badge>
            ))}
          </div>
        )}

        <div className="flex items-center gap-6 pt-4 border-t border-amber-100">
          <button
            onClick={() => onLike(post.id)}
            className={`flex items-center gap-2 text-sm ${post.is_liked ? 'text-red-500' : 'text-amber-600 hover:text-red-500'} transition-colors`}
          >
            <Heart className={`w-5 h-5 ${post.is_liked ? 'fill-current' : ''}`} />
            <span>{post.likes_count || 0}</span>
          </button>
          <button
            onClick={() => navigate(`/community/post/${post.id}`)}
            className="flex items-center gap-2 text-sm text-amber-600 hover:text-amber-800 transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
            <span>{post.comments_count || 0}</span>
          </button>
          <button
            onClick={() => onFavorite(post.id)}
            className={`flex items-center gap-2 text-sm ${post.is_favorited ? 'text-yellow-500' : 'text-amber-600 hover:text-yellow-500'} transition-colors`}
          >
            <Star className={`w-5 h-5 ${post.is_favorited ? 'fill-current' : ''}`} />
            <span>{post.favorites_count || 0}</span>
          </button>
          <ShareButton 
            url={`${window.location.origin}/community/post/${post.id}`}
            title={post.content?.substring(0, 50)}
          />
        </div>
      </CardContent>
    </Card>
  );
};

// 发帖弹窗组件
const CreatePostDialog = ({ open, onOpenChange, newPost, setNewPost, tagInput, setTagInput, addTag, removeTag, onSubmit, categoryTitle, t }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('community.publishTo', { category: categoryTitle || t('community.pageTitle') })}</DialogTitle>
          <DialogDescription>{t('community.shareYourThoughts')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Textarea
            value={newPost.content}
            onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
            placeholder={t('community.sharePlaceholder')}
            className="min-h-32 resize-none"
            data-testid="post-content-input"
          />
          
          <PostMediaUploader
            images={newPost.images}
            onImagesChange={(images) => setNewPost({ ...newPost, images })}
            videoUrl={newPost.video_url}
            onVideoChange={(video_url) => setNewPost({ ...newPost, video_url })}
          />

          <div>
            <div className="flex gap-2 mb-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder={t('community.addTagLabel')}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={addTag}>
                {t('community.addButton')}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {newPost.tags.map((tag, idx) => (
                <Badge 
                  key={idx} 
                  variant="secondary" 
                  className="cursor-pointer hover:bg-gray-300"
                  onClick={() => removeTag(tag)}
                >
                  #{tag} ×
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('community.cancelButton')}
          </Button>
          <Button onClick={onSubmit} data-testid="submit-post-btn">
            {t('community.publishButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CommunityPage;
